import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  User,
  Repository,
  SearchParams,
  ChatMessage,
  Favorite,
  Theme,
  Language,
  ActiveTab,
  AppState
} from '../types';
import apiService from '../services/api';

interface AppStore extends AppState {
  // 用户操作
  setUser: (user: User | null) => void;
  initializeAuth: () => Promise<boolean>;
  login: (account: string, password: string) => Promise<boolean>;
  register: (phone: string, username: string, password: string, qianwen_api_key?: string, github_token?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  updateProfile: (data: any) => Promise<boolean>;
  
  // 搜索操作
  searchProjects: (params: SearchParams, isAuthenticated?: boolean) => Promise<void>;
  setCurrentPage: (page: number) => void;
  setSearchResults: (results: Repository[]) => void;
  setSearchLoading: (loading: boolean) => void;
  clearSearchResults: () => void;
  cancelSearch: () => void;
  
  // AI操作
  addAIMessage: (message: ChatMessage) => void;
  setAILoading: (loading: boolean) => void;
  clearAIMessages: () => void;
  
  // 收藏操作
  loadFavorites: () => Promise<void>;
  toggleFavorite: (project: Repository) => Promise<boolean>;
  isFavorited: (projectFullName: string) => boolean;
  setFavoritesLoading: (loading: boolean) => void;

  // Trending操作
  loadTrendingProjects: (date?: string) => Promise<void>;
  loadTrendingDates: () => Promise<void>;
  setSelectedTrendingDate: (date: string) => void;
  setTrendingLoading: (loading: boolean) => void;
  fetchTodayTrending: () => Promise<boolean>;
  generateAIInterpretations: (date?: string) => Promise<boolean>;
  
  // UI操作
  setTheme: (theme: Theme) => void;
  setLanguage: (language: Language) => void;
  setSidebarOpen: (open: boolean) => void;
  setAISidebarOpen: (open: boolean) => void;
  setActiveTab: (tab: ActiveTab) => void;
  
  // 错误处理
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => {
      const setModuleError = (message: string | null, source?: AppState['errorSource']) =>
        set({ error: message, errorSource: source });

      return {
      // 初始状态
      user: null,
      isAuthenticated: false,
      isLoggingIn: false,
      searchResults: [],
      searchLoading: false,
      searchParams: null,
      totalCount: 0,
      currentPage: 1,
      totalPages: 1,
      aiMessages: [],
      aiLoading: false,
      favorites: [],
      favoritesLoading: false,
      trendingProjects: [],
      trendingLoading: false,
      trendingDates: [],
      selectedTrendingDate: new Date().toISOString().split('T')[0],
      theme: 'light',
      language: 'zh',
      sidebarOpen: false,
      aiSidebarOpen: false,
      activeTab: 'trending',
      error: null,
      errorSource: undefined,

      // 用户操作
      setUser: (user) => {
        set({
          user,
          isAuthenticated: !!user
        });
      },

      // 初始化用户状态
      initializeAuth: async () => {
        try {
          const token = localStorage.getItem('session_token');
          const userStr = localStorage.getItem('user');

          if (token && userStr) {
            try {
              const user = JSON.parse(userStr);
              console.log('🔍 从localStorage恢复用户状态:', user);

              // 先设置用户状态，让用户可以正常使用
              set({
                user: user,
                isAuthenticated: true,
                error: null
              });

              // 后台验证token是否仍然有效（不阻塞用户使用）
              try {
                const response = await apiService.getProfile();
                if (response.success && response.user) {
                  console.log('✅ Token验证成功，更新用户信息');
                  set({
                    user: response.user,
                    isAuthenticated: true,
                    error: null
                  });
                } else {
                  console.log('⚠️ Token验证失败，但保持当前状态，响应:', response);
                  // 不立即清除状态，让用户继续使用，直到真正需要认证的操作时再处理
                }
              } catch (verifyError: any) {
                console.log('⚠️ Token验证请求失败，但保持当前状态');
                console.log('验证错误详情:', {
                  message: verifyError.message,
                  status: verifyError.response?.status,
                  data: verifyError.response?.data
                });
                // 网络错误或服务器问题，不清除用户状态
                // 也不设置错误状态，避免误导用户
              }

              return true;
            } catch (parseError) {
              console.log('❌ 用户数据解析失败，清除本地状态');
              localStorage.removeItem('session_token');
              localStorage.removeItem('user');
              set({
                user: null,
                isAuthenticated: false,
                error: null
              });
              return false;
            }
          } else {
            console.log('📝 未找到本地认证信息');
            set({
              user: null,
              isAuthenticated: false,
              error: null
            });
            return false;
          }
        } catch (error) {
          console.error('❌ 初始化认证状态失败:', error);
          set({
            user: null,
            isAuthenticated: false,
            error: null
          });
          return false;
        }
      },

      login: async (account, password) => {
        const { isLoggingIn } = get();
        if (isLoggingIn) {
          console.log('⚠️ 登录正在进行中，忽略重复请求');
          return false;
        }

        try {
          console.log('🔍 开始登录:', { account, password: '***' });
          set({ error: null, isLoggingIn: true });
          const response = await apiService.login({ account, password });
          console.log('📡 登录响应:', response);

          if (response.success && response.user) {
            console.log('✅ 登录成功:', response.user);
            set({
              user: response.user,
              isAuthenticated: true,
              isLoggingIn: false,
              error: null
            });
            return true;
          } else {
            console.log('❌ 登录失败 - 响应错误:', response.error);
            set({
              error: response.error || '登录失败',
              isLoggingIn: false
            });
            return false;
          }
        } catch (error: any) {
          console.error('❌ 登录异常:', error);
          console.error('错误详情:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status
          });

          // 登录失败时清除可能存在的旧认证信息
          if (error.response?.status === 400 || error.response?.status === 401) {
            localStorage.removeItem('session_token');
            localStorage.removeItem('user');
          }

          const errorMessage = error.response?.data?.error || '登录失败，请稍后重试';
          set({
            error: errorMessage,
            isLoggingIn: false
          });
          return false;
        }
      },

      register: async (phone, username, password, qianwen_api_key, github_token) => {
        try {
          set({ error: null });
          const response = await apiService.register({
            phone,
            username,
            password,
            qianwen_api_key,
            github_token
          });

          if (response.success && response.user) {
            set({
              user: response.user,
              isAuthenticated: true
            });
            return true;
          } else {
            set({ error: response.error || '注册失败' });
            return false;
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || '注册失败，请稍后重试';
          set({ error: errorMessage });
          return false;
        }
      },

      logout: async () => {
        try {
          await apiService.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({ 
            user: null, 
            isAuthenticated: false,
            favorites: [],
            aiMessages: []
          });
        }
      },

      updateProfile: async (data) => {
        try {
          set({ error: null });
          const response = await apiService.updateProfile(data);

          if (response.success && response.user) {
            set({ user: response.user });
            return true;
          } else {
            const message = response.error || '更新失败';
            set({ error: message });
            // 将错误抛出，方便调用方在本地展示
            throw new Error(message);
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || error.message || '更新失败，请稍后重试';
          set({ error: errorMessage });
          // 继续抛出，交由调用方处理
          throw new Error(errorMessage);
        }
      },

      // 搜索操作
      searchProjects: async (params, isAuthenticated) => {
        try {
          set({ searchLoading: true, error: null });

          // 重置页码为1，因为这是新的搜索
          const searchParams = { ...params, page: 1 };
          // 如果没有传递isAuthenticated参数，使用当前状态
          const authStatus = isAuthenticated !== undefined ? isAuthenticated : get().isAuthenticated;
          const result = await apiService.searchProjects(searchParams, authStatus);

          set({
            searchResults: result.repositories,
            searchParams: searchParams,
            totalCount: result.total_count,
            currentPage: 1,
            totalPages: result.pagination.total_pages,
            searchLoading: false
          });
        } catch (error: any) {
          // 如果是用户取消的请求，不显示错误
          if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
            set({ searchLoading: false });
            return;
          }

          const errorMessage = error.response?.data?.error || '搜索失败，请稍后重试';
          set({
            error: errorMessage,
            searchLoading: false
          });
        }
      },

      // 设置当前页码（用于分页，不重新搜索）
      setCurrentPage: async (page) => {
        const state = get();
        if (!state.searchParams || page === state.currentPage) {
          return;
        }

        try {
          set({ searchLoading: true, error: null });

          // 使用当前搜索参数，只改变页码
          const params = { ...state.searchParams, page };
          const result = await apiService.searchProjects(params, state.isAuthenticated);

          set({
            searchResults: result.repositories,
            currentPage: page,
            searchLoading: false
          });
        } catch (error: any) {
          // 如果是用户取消的请求，不显示错误
          if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
            set({ searchLoading: false });
            return;
          }

          const errorMessage = error.response?.data?.error || '加载页面失败，请稍后重试';
          set({
            error: errorMessage,
            searchLoading: false
          });
        }
      },

      setSearchResults: (results) => set({ searchResults: results }),
      setSearchLoading: (loading) => set({ searchLoading: loading }),
      clearSearchResults: () => set({
        searchResults: [],
        searchParams: null,
        totalCount: 0,
        currentPage: 1,
        totalPages: 1
      }),

      // 取消搜索
      cancelSearch: () => {
        // 取消API请求
        apiService.cancelSearch();

        set({
          searchLoading: false,
          error: null
        });
      },

      // AI操作
      addAIMessage: (message) => {
        set((state) => ({
          aiMessages: [...state.aiMessages, message]
        }));
      },

      setAILoading: (loading) => set({ aiLoading: loading }),
      clearAIMessages: () => set({ aiMessages: [] }),

      // 收藏操作
      loadFavorites: async () => {
        try {
          // 检查用户是否已登录
          const currentState = get();
          if (!currentState.isAuthenticated || !currentState.user) {
            console.log('⚠️ 用户未登录，跳过收藏列表加载');
            return;
          }

          set({ favoritesLoading: true, error: null });
          const response = await apiService.getFavorites();
          console.log('📡 收藏列表响应:', response);

          if (response.success && response.favorites) {
            console.log(`✅ 收藏列表加载成功，共 ${response.favorites.length} 个收藏`);
            set({
              favorites: response.favorites,
              favoritesLoading: false
            });
          } else {
            console.log('❌ 收藏列表加载失败:', response.error);
            // 如果是认证错误，不显示错误信息给用户
            if (response.error?.includes('登录') || response.error?.includes('认证')) {
              set({
                favoritesLoading: false
              });
            } else {
              set({
                error: response.error || '加载收藏失败',
                favoritesLoading: false
              });
            }
          }
        } catch (error: any) {
          console.error('❌ 收藏列表加载异常:', error);

          // 如果是401错误（未认证），不显示错误信息
          if (error.response?.status === 401) {
            console.log('⚠️ 认证失效，清除用户状态');
            set({
              user: null,
              isAuthenticated: false,
              favoritesLoading: false,
              favorites: []
            });
          } else {
            const errorMessage = error.response?.data?.error || '加载收藏失败';
            set({
              error: errorMessage,
              favoritesLoading: false
            });
          }
        }
      },

      toggleFavorite: async (project) => {
        try {
          const response = await apiService.toggleFavorite(project);

          if (response.success) {
            // 重新加载收藏列表
            await get().loadFavorites();
            return true;
          } else {
            set({ error: response.error || '操作失败' });
            return false;
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || '操作失败，请稍后重试';
          set({ error: errorMessage });
          return false;
        }
      },

      // 检查项目是否已收藏
      isFavorited: (projectFullName: string) => {
        const state = get();
        return state.favorites.some(fav => fav.project_full_name === projectFullName);
      },

      setFavoritesLoading: (loading) => set({ favoritesLoading: loading }),

      // Trending操作
      loadTrendingProjects: async (date) => {
        try {
          set({ trendingLoading: true });

          const targetDate = date || get().selectedTrendingDate;
          const response = await apiService.getTrendingProjects(targetDate);

          if (response.success) {
            set({
              trendingProjects: response.projects || [],
              trendingLoading: false
            });
          } else {
            set({
              error: response.error || '获取Trending项目失败',
              trendingLoading: false
            });
          }
        } catch (error: any) {
          console.error('获取Trending项目失败:', error);
          set({
            error: error.response?.data?.error || '获取Trending项目失败',
            trendingLoading: false
          });
        }
      },

      loadTrendingDates: async () => {
        try {
          // 使用新的API获取可用日期（支持7天历史数据）
          const response = await apiService.getAvailableDates(7);

          if (response.success) {
            set({ trendingDates: response.dates || [] });
          } else {
            set({ error: response.error || '获取历史日期失败' });
          }
        } catch (error: any) {
          console.error('获取历史日期失败:', error);
          set({ error: error.response?.data?.error || '获取历史日期失败' });
        }
      },

      setSelectedTrendingDate: (date) => {
        set({ selectedTrendingDate: date });
        // 自动加载该日期的项目
        get().loadTrendingProjects(date);
      },

      setTrendingLoading: (loading) => set({ trendingLoading: loading }),

      fetchTodayTrending: async () => {
        try {
          const response = await apiService.fetchTodayTrending();

          if (response.success) {
            // 重新加载今日数据
            await get().loadTrendingProjects();
            await get().loadTrendingDates();
            return true;
          } else {
            set({ error: response.error || '获取今日数据失败' });
            return false;
          }
        } catch (error: any) {
          console.error('获取今日数据失败:', error);
          set({ error: error.response?.data?.error || '获取今日数据失败' });
          return false;
        }
      },

      generateAIInterpretations: async (date) => {
        try {
          const response = await apiService.generateAIInterpretations(date);

          if (response.success) {
            // 重新加载数据以获取AI解读
            await get().loadTrendingProjects(date);
            return true;
          } else {
            set({ error: response.error || '生成AI解读失败' });
            return false;
          }
        } catch (error: any) {
          console.error('生成AI解读失败:', error);
          set({ error: error.response?.data?.error || '生成AI解读失败' });
          return false;
        }
      },

      // UI操作
      setTheme: (theme) => set({ theme }),
      setLanguage: (language) => set({ language }),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setAISidebarOpen: (open) => set({ aiSidebarOpen: open }),
      setActiveTab: (tab) => set({ activeTab: tab }),

      // 错误处理
      setError: (error) => set({ error }),
      clearError: () => setModuleError(null),
    };
    },
    {
      name: 'github-search-store',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        theme: state.theme,
        language: state.language,
      }),
    }
  )
);
